import { BaseResponse } from '@utils/types/shared/base.ts';

export type QueueStatus =
  | 'queued'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'dead_letter';

export type PriorityQueueItem = {
  id: string;
  zoom_id: string;
  priority: number;
  org_priority: number;
  status: QueueStatus;
  created_at: string;
  processing_started_at: string | null;
  completed_at: string | null;
  retry_count: number;
  error_message: string | null;
  error_category: string | null;
  payload?: Record<string, unknown>;
  organization_id: number | null;
  organization_name: string | null;
};

export type StatusCount = {
  status: QueueStatus;
  count: number;
  oldest_item_age_minutes: number | null;
};

export type OrgStatusCount = {
  status: QueueStatus;
  org_priority: number;
  count: number;
};

export type GPUConfig = {
  enabled: boolean;
  min_gpus: number;
  max_gpus: number;
  sessions_per_gpu: number;
  time_slicing: boolean;
  enable_capacity_gating: boolean;
  enable_projected_capacity: boolean;
  projected_capacity_factor: number;
  capacity_buffer_factor: number;
  scale_up_eta_minutes: number;
  scale_down_delay_seconds: number;
  max_queue_size_at_capacity: number;
  max_queue_age_alert_seconds: number;
  backpressure_sleep_seconds: number;
  wfq_reference_priority: number;
  wfq_weight_divisor: number;
  wfq_min_weight: number;
  wfq_max_weight: number;
};

export type QueueSummary = {
  status_counts: StatusCount[];
  total_items: number;
  stuck_processing_count: number;
  ready_for_retry_count: number;
  gpu_config: GPUConfig;
  org_counts: OrgStatusCount[] | null;
};

export type PriorityQueueListResponse = BaseResponse<PriorityQueueItem[]>;
export type QueueSummaryResponse = QueueSummary;
export type PriorityQueueItemResponse = BaseResponse<PriorityQueueItem>;

export type BulkActionResponse = {
  success: boolean;
  affected_count: number;
  message?: string;
  item_ids?: number[];
  skipped_count?: number;
};

export type BulkActionResponseData = BaseResponse<BulkActionResponse>;

export type SetPriorityRequest = {
  org_priority: number;
  reason: string;
  confirm_deprioritise?: boolean;
};

export type SetPriorityResponse = {
  success: boolean;
  message: string;
  item: PriorityQueueItem | null;
};

export type BulkSetPriorityRequest = {
  org_priority: number;
  reason: string;
  confirm_deprioritise?: boolean;
  include_fair_use?: boolean;
  item_ids?: number[];
  organization_id?: number;
  session_timestamp_after?: string;
  session_timestamp_before?: string;
};

export type BulkSetPriorityResponse = BulkActionResponse;

export const PRIORITY_CLIFF = 300;
